import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  DURATION,
  EASE,
  MASK_SPLIT_DURATION,
  TEXT_STAGGER,
} from "./animation-config.js";

gsap.registerPlugin(ScrollTrigger);

const IMAGE_X_RIGHT = "50%";
const TEXT_X_LEFT = "-100vw";
const MASK_SPLIT_IMAGE_SCALE = 1.12;
const TEXT_X = "50%";

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

const desktopQuery = window.matchMedia("(min-width: 1261px)");

function createScrollTrigger(section, onEnter) {
  ScrollTrigger.create({
    trigger: section,
    start: "top 70%",
    once: true,
    onEnter: () => {
      section.classList.add("is-in-view");
      onEnter();
    },
  });
}

function initImageRight(section) {
  const contentInner = section.querySelector(".split-section__content-inner");
  const textBottom = section.querySelector(".karriere-about__text-bottom");
  const media = section.querySelector(".split-section__media");

  if (!contentInner && !textBottom && !media) {
    return;
  }

  if (reducedMotion || !desktopQuery.matches) {
    section.classList.add("is-in-view");
    return;
  }

  if (contentInner) {
    gsap.set(contentInner, { x: TEXT_X_LEFT, opacity: 0 });
  }

  if (textBottom) {
    gsap.set(textBottom, { x: TEXT_X_LEFT, opacity: 0 });
  }

  if (media) {
    gsap.set(media, { x: IMAGE_X_RIGHT });
  }

  ScrollTrigger.create({
    trigger: section,
    start: "top 70%",
    once: true,
    onEnter: () => {
      section.classList.add("is-revealing");

      const timeline = gsap.timeline({
        defaults: { duration: DURATION, ease: EASE },
        onComplete: () => {
          section.classList.remove("is-revealing");
          section.classList.add("is-in-view");

          if (contentInner) {
            gsap.set(contentInner, { clearProps: "all" });
          }

          if (textBottom) {
            gsap.set(textBottom, { clearProps: "all" });
          }

          if (media) {
            gsap.set(media, { clearProps: "all" });
          }
        },
      });

      if (contentInner) {
        timeline.to(contentInner, { x: 0, opacity: 1 }, 0);
      }

      if (media) {
        timeline.to(media, { x: 0 }, 0);
      }

      if (textBottom) {
        timeline.to(textBottom, { x: 0, opacity: 1 }, TEXT_STAGGER);
      }
    },
  });
}

function initImageLeftMaskSplit(section) {
  const image = section.querySelector(".split-section__image");
  const headline = section.querySelector(".split-section__headline");
  const subtitle = section.querySelector(".split-section__subtitle");
  const text = section.querySelector(".split-section__text");
  const textTargets = [headline, subtitle, text].filter(Boolean);

  if (!image && !textTargets.length) {
    return;
  }

  if (reducedMotion || !desktopQuery.matches) {
    if (image) {
      gsap.set(image, { scale: 1 });
    }

    gsap.set(textTargets, { x: 0, opacity: 1 });
    section.classList.add("is-in-view");
    return;
  }

  if (image) {
    gsap.set(image, { scale: MASK_SPLIT_IMAGE_SCALE });
  }

  textTargets.forEach((element) => {
    gsap.set(element, { x: TEXT_X, opacity: 0 });
  });

  createScrollTrigger(section, () => {
    const timeline = gsap.timeline({
      defaults: { duration: MASK_SPLIT_DURATION, ease: EASE },
    });

    if (image) {
      timeline.to(image, { scale: 1 }, 0);
    }

    if (headline) {
      timeline.to(headline, { x: 0, opacity: 1 }, 0);
    }

    if (subtitle) {
      timeline.to(subtitle, { x: 0, opacity: 1 }, TEXT_STAGGER);
    }

    if (text) {
      timeline.to(text, { x: 0, opacity: 1 }, TEXT_STAGGER * 2);
    }
  });
}

const variants = [
  {
    match: (section) =>
      section.classList.contains("split-section--image-left") &&
      (section.classList.contains("split-section--mask-split") ||
        section.classList.contains("split-section--mask-split-reverse")),
    init: initImageLeftMaskSplit,
  },
  {
    match: (section) =>
      section.classList.contains("split-section--image-right"),
    init: initImageRight,
  },
];

document.querySelectorAll(".split-section").forEach((section) => {
  const variant = variants.find(({ match }) => match(section));

  if (variant) {
    variant.init(section);
  }
});

window.addEventListener(
  "load",
  () => {
    ScrollTrigger.refresh();
  },
  { once: true },
);
